export type TocLevel = 2 | 3 | 4;

export type TocItem = {
  id: string;
  text: string;
  level: TocLevel;
  line: number;
};

const META_SECTION_HEADINGS = new Set([
  "about the author",
  "frequently asked questions",
  "references",
  "sources",
  "related reading",
  "related articles",
  "about the writer",
  "عن الكاتب",
  "أسئلة شائعة",
  "المراجع",
  "المصادر",
]);

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");
}

/** Converts visible heading markup to stable, human-readable text. */
export function getTocHeadingText(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value)
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/(\*\*|__|\*|_|`)/g, "")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+#+\s*$/, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  if (Array.isArray(value)) {
    return value.map(getTocHeadingText).join("").replace(/\s+/g, " ").trim();
  }

  if (value && typeof value === "object" && "props" in value) {
    const props = (value as { props?: { children?: unknown } }).props;
    return getTocHeadingText(props?.children);
  }

  return "";
}

/** Preserves Unicode letters so Arabic heading anchors remain meaningful and linkable. */
export function headingToId(text: string): string {
  const normalized = decodeHtmlEntities(getTocHeadingText(text))
    .normalize("NFKD")
    .toLocaleLowerCase()
    // Latin extended + Arabic ranges cover the site's English and MSA routes
    // without requiring an ES2018 Unicode-property-regex build target.
    .replace(/[^a-z0-9\u00c0-\uffff]+/gi, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || "section";
}

function isMetaSectionHeading(text: string): boolean {
  return META_SECTION_HEADINGS.has(getTocHeadingText(text).toLocaleLowerCase());
}

/**
 * Keeps the parser and React heading renderer on the same anchor contract.
 * H3/H4 items belonging to FAQ, sources, or author sections are deliberately excluded:
 * they are support content rather than the article's primary reading route.
 */
export function createTocHeadingIdResolver() {
  const counts = new Map<string, number>();
  let skipNestedHeadings = false;

  return (level: TocLevel, text: string): string | null => {
    const headingText = getTocHeadingText(text);
    if (!headingText) return null;

    if (level === 2) {
      skipNestedHeadings = isMetaSectionHeading(headingText);
    }

    if ((level === 2 && skipNestedHeadings) || ((level === 3 || level === 4) && skipNestedHeadings)) {
      return null;
    }

    const baseId = headingToId(headingText);
    const count = (counts.get(baseId) ?? 0) + 1;
    counts.set(baseId, count);
    return count === 1 ? baseId : `${baseId}-${count}`;
  };
}

/**
 * Converts legacy raw HTML heading tags to Markdown headings before Streamdown renders.
 * This lets historic posts share the same heading IDs and interactive ToC as Markdown posts.
 */
export function normalizeContentForToc(content: string): string {
  return content.replace(/<h([234])\b[^>]*>([\s\S]*?)<\/h\1>/gi, (_match, level: string, heading: string) => {
    const text = getTocHeadingText(decodeHtmlEntities(heading));
    return text ? `${"#".repeat(Number(level))} ${text}` : "";
  });
}

/** Parses primary article H2/H3/H4 headings, ignoring fenced code samples and support sections. */
export function parseTocItems(content: string): TocItem[] {
  const normalizedContent = normalizeContentForToc(content);
  const resolver = createTocHeadingIdResolver();
  const items: TocItem[] = [];
  let insideFence = false;

  normalizedContent.split("\n").forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (/^(```|~~~)/.test(line)) {
      insideFence = !insideFence;
      return;
    }
    if (insideFence) return;

    const match = line.match(/^(#{2,4})\s+(.+?)\s*#*\s*$/);
    if (!match) return;

    const level = match[1].length as TocLevel;
    const text = getTocHeadingText(match[2]);
    const id = resolver(level, text);
    if (id) items.push({ id, text, level, line: index + 1 });
  });

  return items;
}
